import { useCallback, useEffect, useState } from 'react';
import {
  accountAcraStatus,
  acraRegistryFromEntity,
  coerceAcraStatus,
  lookupAcraRegistry,
  sanitizeIntelProfileRegistry,
} from '../../lib/acraRegistry';
import { getAcraEntityByUen } from '../../lib/acraEntitiesDb';
import { getDbSupabase } from '../../lib/supabase';
import { useCrmData } from '../../contexts/CrmDataContext';
import type { AccountIntelRegistry, Client } from '../../types/crm';
import { useAcraSyncRealtime } from '../../hooks/useAcraSyncRealtime';
import { AcraStatusBadge } from './AcraStatusBadge';
import { isUenMarkedNa } from '../../lib/uenValue';

type AccountAcraStatusProps = {
  client: Client;
  className?: string;
  /** Persist fetched ACRA registry (status + registered address) to intel_profile_json. */
  persist?: boolean;
  /** Preloaded status from account list batch lookup. */
  prefetchedStatus?: string | null;
};

export function AccountAcraStatus({
  client,
  className = '',
  persist = true,
  prefetchedStatus = null,
}: AccountAcraStatusProps) {
  const { refresh } = useCrmData();
  const cachedStatus = accountAcraStatus(client);
  const cachedIntelUen = client.intel_profile_json?.uen?.trim().toUpperCase() ?? '';
  const hasUen = Boolean(client.uen?.trim()) && !isUenMarkedNa(client.uen);
  const [status, setStatus] = useState<string | null>(() => {
    const live = coerceAcraStatus(prefetchedStatus);
    if (live) return live;
    if (hasUen) return null;
    return cachedStatus;
  });
  const [refreshToken, setRefreshToken] = useState(0);

  const reload = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  useAcraSyncRealtime(reload, { onlyOnComplete: true, debounceMs: 2000 });

  useEffect(() => {
    const live = coerceAcraStatus(prefetchedStatus);
    if (live) {
      setStatus(live);
      return;
    }
    if (!hasUen) {
      setStatus(cachedStatus);
    }
  }, [cachedStatus, client.id, client.uen, hasUen, prefetchedStatus]);

  useEffect(() => {
    const uen = client.uen?.trim() ?? '';
    const name = client.company_name?.trim() ?? '';
    if (isUenMarkedNa(uen)) {
      setStatus(null);
      return;
    }
    if (!uen && name.length < 2) return;

    let cancelled = false;

    const applyRegistry = (registry: AccountIntelRegistry | null) => {
      const nextStatus = coerceAcraStatus(registry?.status);
      if (cancelled || !nextStatus) return;
      setStatus(nextStatus);
      if (!persist || !client.id) return;

      const mergedBase = sanitizeIntelProfileRegistry(
        registry,
        registry?.uen || client.uen?.trim() || '',
      );
      if (!mergedBase) return;

      // Open-data refresh has no paid-up capital — keep any previously enriched value.
      const merged: AccountIntelRegistry = {
        ...mergedBase,
        paidUpCapital: mergedBase.paidUpCapital ?? client.intel_profile_json?.paidUpCapital ?? null,
        currency: mergedBase.currency ?? client.intel_profile_json?.currency ?? null,
      };

      const existingStatus = coerceAcraStatus(client.intel_profile_json?.status) ?? '';
      const existingUen = client.intel_profile_json?.uen?.trim().toUpperCase() ?? '';
      const nextUen = merged.uen?.trim().toUpperCase() ?? '';
      const existingAddress = client.intel_profile_json?.registeredAddress?.trim() ?? '';
      const nextAddress = merged.registeredAddress?.trim() ?? '';
      const existingPaidUp = client.intel_profile_json?.paidUpCapital?.trim() ?? '';
      const nextPaidUp = merged.paidUpCapital?.trim() ?? '';

      const registryChanged =
        existingStatus !== nextStatus ||
        existingUen !== nextUen ||
        existingAddress !== nextAddress ||
        existingPaidUp !== nextPaidUp;

      if (!registryChanged) return;

      void getDbSupabase()
        .from('clients')
        .update({ intel_profile_json: merged })
        .eq('id', client.id)
        .then(({ error }) => {
          if (cancelled || error) return;
          void refresh({ silent: true, slices: ['clients'] });
        });
    };

    void (async () => {
      try {
        if (uen) {
          const local = await getAcraEntityByUen(uen);
          if (local) {
            applyRegistry(acraRegistryFromEntity(local));
            return;
          }
        }
        if (!uen && cachedStatus) return;
        const registry = await lookupAcraRegistry(name, uen);
        applyRegistry(registry);
      } catch {
        // Ignore lookup failures — badge falls back to cached status or hides.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    cachedIntelUen,
    cachedStatus,
    client.company_name,
    client.id,
    client.intel_profile_json?.paidUpCapital,
    client.intel_profile_json?.registeredAddress,
    client.intel_profile_json?.status,
    client.intel_profile_json?.uen,
    client.uen,
    persist,
    refresh,
    refreshToken,
  ]);

  if (!status) return null;
  return <AcraStatusBadge status={status} className={className} />;
}
